<template>
    <section class="bg-gray-50 min-h-screen py-10">
        <div class="container mx-auto px-4">
            <!-- Title and Subtitle -->
            <h1 class="text-4xl font-extrabold text-center text-green-900 mb-4">
                Blood Donor Platform
            </h1>
            <p class="text-lg text-center text-gray-700 mb-10">
                Join us in saving lives by becoming a blood donor today.
            </p>

            <!-- Hero Section -->
            <div class="flex flex-col lg:flex-row items-center justify-between bg-white border rounded-xl p-6 mb-10">
                <div class="lg:w-1/2 mb-6 lg:mb-0">
                    <img src="https://via.placeholder.com/800x450" alt="Placeholder Image"
                        class="rounded-lg shadow-lg w-full">
                </div>
                <div class="lg:w-1/2 lg:pl-10 text-center lg:text-left">
                    <h2 class="text-3xl font-bold text-green-900 mb-4">
                        Save Lives, Become a Hero
                    </h2>
                    <p class="text-gray-700 mb-6">
                        Donating blood is an easy way to make a significant impact on someone's life. Join our platform
                        and connect with those in need.
                    </p>
                    <button @click="toggleDonorModal"
                        class="bg-gradient-to-r from-green-900 to-green-800 text-white px-8 py-4 rounded-full text-lg shadow-lg hover:scale-105 transform transition">
                        + Register as a Blood Donor
                    </button>
                </div>
            </div>

            <!-- Tabs for Blood Groups -->
            <div class="mb-12">
                <div class="flex justify-center space-x-4 mb-6">
                    <button v-for="group in bloodGroups" :key="group" @click="currentGroup = group"
                        class="px-6 py-2 border rounded-full text-lg font-semibold" :class="{
                            'bg-green-900 text-white': currentGroup === group,
                            'bg-gray-200 text-gray-700': currentGroup !== group
                        }">
                        {{ group }}
                    </button>
                </div>

                <!-- Donor Cards -->
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    <div v-for="donor in filteredDonors(currentGroup)" :key="donor.id"
                        class="p-6 bg-white rounded-xl border hover:shadow-lg transform hover:-translate-y-2 transition cursor-pointer"
                        @click="viewDonorDetails(donor)">
                        <div class="text-center">
                            <img src="https://via.placeholder.com/150" alt="Donor Image"
                                class="w-28 h-28 rounded-full mx-auto mb-4 shadow-lg" />
                            <h3 class="font-semibold text-lg text-green-900 mb-2">{{ donor.name }}</h3>
                            <p class="text-gray-500">{{ donor.contact }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Donor Details Modal -->
        <div v-if="selectedDonor" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div class="bg-white bg-opacity-80 backdrop-blur-lg shadow-lg rounded-xl w-full max-w-lg p-6 relative">
                <button @click="selectedDonor = null"
                    class="absolute top-4 right-4 text-red-600 text-2xl hover:scale-110 transform transition">
                    ✖
                </button>
                <img src="https://via.placeholder.com/150" alt="Donor Image"
                    class="w-28 h-28 rounded-full mx-auto mb-4 shadow-lg" />
                <h3 class="text-2xl font-bold text-center text-gray-800 mb-2">{{ selectedDonor.name }}</h3>
                <div class="text-center text-gray-600">
                    <p><strong>Medical Info:</strong> {{ selectedDonor.medicalInfo || "N/A" }}</p>
                    <p><strong>Primary Contact:</strong> {{ selectedDonor.contact }}</p>
                    <p><strong>Secondary Contact:</strong> {{ selectedDonor.secondaryContact || "N/A" }}</p>
                </div>
            </div>
        </div>

        <!-- Become a Donor Modal -->
        <div v-if="showDonorModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div class="bg-white bg-opacity-90 backdrop-blur-lg shadow-lg rounded-xl w-full max-w-xl p-6 relative">
                <button @click="toggleDonorModal"
                    class="absolute top-4 right-4 text-red-600 text-2xl hover:scale-110 transform transition">
                    ✖
                </button>
                <h3 class="text-xl font-bold text-gray-700 mb-6">Become a Blood Donor</h3>
                <form @submit.prevent="addDonor">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label class="block text-gray-600 mb-1">Full Name</label>
                            <input type="text" v-model="newDonor.name"
                                class="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-green-900"
                                required />
                        </div>
                        <div>
                            <label class="block text-gray-600 mb-1">Blood Group</label>
                            <select v-model="newDonor.bloodGroup"
                                class="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-green-900"
                                required>
                                <option value="" disabled>Select</option>
                                <option v-for="group in bloodGroups" :key="group">{{ group }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-gray-600 mb-1">Phone</label>
                            <input type="tel" v-model="newDonor.contact"
                                class="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-green-900"
                                required />
                        </div>
                        <div>
                            <label class="block text-gray-600 mb-1">Secondary Phone</label>
                            <input type="tel" v-model="newDonor.secondaryContact"
                                class="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-green-900" />
                        </div>
                    </div>
                    <div class="mb-4">
                        <label class="block text-gray-600 mb-1">Email</label>
                        <input type="email" v-model="newDonor.email"
                            class="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-green-900" />
                    </div>
                    <div class="mb-4">
                        <label class="block text-gray-600 mb-1">Medical Information</label>
                        <textarea v-model="newDonor.medicalInfo"
                            class="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-green-900"></textarea>
                    </div>
                    <div class="mb-4">
                        <label class="block text-gray-600 mb-1">Address</label>
                        <textarea v-model="newDonor.address"
                            class="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-green-900"></textarea>
                    </div>
                    <button type="submit"
                        class="w-full bg-gradient-to-r from-green-900 to-green-900 text-white px-6 py-3 rounded-lg text-lg shadow-lg hover:scale-105 transform transition">
                        Submit
                    </button>
                </form>
            </div>
        </div>
    </section>
</template>

<script>
import { ref } from "vue";

export default {
    name: "BloodDonorPlatform",
    setup() {
        const bloodGroups = ["A+", "B+", "O+", "AB+", "A-", "B-", "O-", "AB-"];
        const donors = ref([
            { id: 1, name: "John Doe", bloodGroup: "A+", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 1, name: "John Doe", bloodGroup: "A+", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 1, name: "John Doe", bloodGroup: "A-", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 1, name: "John Doe", bloodGroup: "A-", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 1, name: "John Doe", bloodGroup: "A+", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 2, name: "John Doe", bloodGroup: "A+", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 3, name: "John Doe", bloodGroup: "A-", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 4, name: "John Doe", bloodGroup: "A+", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 5, name: "John Doe", bloodGroup: "A+", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 6, name: "John Doe", bloodGroup: "A-", contact: "1234567890", secondaryContact: "0987654321", medicalInfo: "Diabetes" },
            { id: 7, name: "Jane Smith", bloodGroup: "B+", contact: "2234567890" },
            { id: 8, name: "Jane Smith", bloodGroup: "B+", contact: "2234567890" },
            { id: 9, name: "Jane Smith", bloodGroup: "B+", contact: "2234567890" },
            { id: 10, name: "Jane Smith", bloodGroup: "B+", contact: "2234567890" },
            { id: 11, name: "Jane Smith", bloodGroup: "B-", contact: "2234567890" },
            { id: 12, name: "Jane Smith", bloodGroup: "B-", contact: "2234567890" },
            { id: 13, name: "Jane Smith", bloodGroup: "B-", contact: "2234567890" },
            { id: 14, name: "Jane Smith", bloodGroup: "B-", contact: "2234567890" },
            { id: 15, name: "Robert Brown", bloodGroup: "O+", contact: "3234567890", medicalInfo: "High BP" },
            { id: 16, name: "Robert Brown", bloodGroup: "O+", contact: "3234567890", medicalInfo: "High BP" },
            { id: 17, name: "Robert Brown", bloodGroup: "O-", contact: "3234567890", medicalInfo: "High BP" },
            { id: 18, name: "Robert Brown", bloodGroup: "O-", contact: "3234567890", medicalInfo: "High BP" },
            { id: 19, name: "Robert Brown", bloodGroup: "O-", contact: "3234567890", medicalInfo: "High BP" },
            { id: 20, name: "Robert Brown", bloodGroup: "O-", contact: "3234567890", medicalInfo: "High BP" },
            { id: 21, name: "Emily Davis", bloodGroup: "AB+", contact: "4234567890" },
            { id: 22, name: "Emily Davis", bloodGroup: "AB+", contact: "4234567890" },
            { id: 23, name: "Emily Davis", bloodGroup: "AB+", contact: "4234567890" },
            { id: 25, name: "Emily Davis", bloodGroup: "AB-", contact: "4234567890" },
            { id: 24, name: "Emily Davis", bloodGroup: "AB-", contact: "4234567890" },
            { id: 25, name: "Emily Davis", bloodGroup: "AB-", contact: "4234567890" },
            { id: 26, name: "Emily Davis", bloodGroup: "AB-", contact: "4234567890" },
        ]);
        const newDonor = ref({});
        const currentGroup = ref("A+");
        const selectedDonor = ref(null);
        const showDonorModal = ref(false);

        const filteredDonors = (group) => donors.value.filter((donor) => donor.bloodGroup === group);

        const viewDonorDetails = (donor) => (selectedDonor.value = donor);

        const toggleDonorModal = () => (showDonorModal.value = !showDonorModal.value);

        const addDonor = () => donors.value.push({ ...newDonor.value, id: Date.now() });

        return { bloodGroups, donors, newDonor, currentGroup, selectedDonor, showDonorModal, filteredDonors, viewDonorDetails, toggleDonorModal, addDonor };
    },
};
</script>
